import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { IssuesTooltip, formatDateRange } from "./ScheduleIssuesPill";
import WarningIcon from "@mui/icons-material/Warning";
import { formatDateReadable } from "../../requests/utilities/helpers";

export default function ActualIssuesPill({ audit, startDate, endDate }) {
  const [open, setOpen] = useState(false);
  const { data, loading, error, retry } = audit;
  const count = data?.total_violations;
  const label = loading ? "Checking issues…" : error ? "Issues unavailable" : `${count} ${count === 1 ? "issue" : "issues"}`;
  const warning = count > 0 || error;
  const [hovered, setHovered] = useState(false);
  const categories = Object.values((data?.groups || []).reduce((result, group) => {
    const key = group.regulation_type;
    if (!result[key]) result[key] = { name: group.regulation_type_name, count: 0 };
    result[key].count += group.violation_count;
    return result;
  }, {}));
  const range = formatDateRange(new Date(`${startDate}T12:00:00`), new Date(`${endDate}T12:00:00`));
  const summary = (
    <div>
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-3.5 py-2.5">
        <span className="text-[13px] font-semibold text-slate-900">{data ? `${count} actual ${count === 1 ? "issue" : "issues"}` : label}</span>
        <span className="text-[11px] text-slate-500 whitespace-nowrap">{range}</span>
      </div>
      <div className="py-1 max-h-64 overflow-y-auto">
        {categories.map((category) => <div key={category.name} className="flex items-center justify-between gap-3 px-3.5 py-1.5 text-[13px]">
          <span className="flex items-center gap-2"><span className="h-2 w-2 shrink-0 rounded-full bg-rose-600" />{category.name}</span>
          <span className="font-semibold text-slate-900">{category.count}</span>
        </div>)}
        {!categories.length && <p className="px-3.5 py-2 text-xs text-slate-500">{loading ? "Checking completed time entries…" : error ? "Audit unavailable. Click to retry." : "No violations found in completed time entries."}</p>}
      </div>
      <div className="border-t border-slate-100 px-3.5 py-2 text-[11px] text-slate-500">Click to view violation details</div>
    </div>
  );
  return (
    <>
      <IssuesTooltip title={summary} placement="bottom-end" open={hovered && !open} onOpen={() => setHovered(true)} onClose={() => setHovered(false)}>
      <button
        type="button" aria-haspopup="dialog" onClick={() => { setHovered(false); setOpen(true); }}
        className={`inline-flex items-center gap-2 h-9 px-3.5 whitespace-nowrap rounded-lg border transition-all duration-200 ${warning
          ? "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
          : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"}`}
      >
        <WarningIcon style={{ fontSize: "16px" }} />
        <span className="text-[13px] font-medium">{label}</span>
      </button>
      </IssuesTooltip>
      <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="actual-issues-title">
        <Box role="dialog" aria-modal="true" aria-labelledby="actual-issues-title" sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: 800, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh", overflowY: "auto",
          bgcolor: "background.paper", border: "2px solid #E2E8F0", borderRadius: "15px", boxShadow: 24, p: { xs: 2, sm: 3 },
        }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="actual-issues-title" className="text-xl font-semibold text-slate-900">Actual labor law violations</h2>
              <p className="mt-1 text-sm text-slate-500">{formatDateReadable(startDate)} – {formatDateReadable(endDate)}</p>
            </div>
            <IconButton aria-label="Close actual issues" size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
          </div>
          {loading && <p role="status" className="py-8 text-sm text-slate-500">Checking completed time entries…</p>}
          {error && <div role="alert" className="mt-5 text-sm text-amber-800">
            Unable to load this week's issues.
            <button type="button" onClick={retry} className="ml-2 font-semibold underline">Try again</button>
          </div>}
          {data && <div className="mt-5">
            <p className="text-sm font-medium text-slate-900">{count ? `${label} found` : "No labor law violations found for this week."}</p>
            <p className="mt-1 text-xs text-slate-500">Based on completed time entries and supported rules. Weekly findings appear on the date assigned by the compliance audit.</p>
            <div className="mt-4 space-y-4">
              {data.groups.map((group, index) => <section key={`${group.regulation_type}-${index}`} className="overflow-hidden rounded-lg border border-slate-200">
                <div className="flex justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="text-sm font-semibold text-slate-900">{group.regulation_type_name}</h3>
                  <span className="inline-flex h-6 min-w-[24px] shrink-0 self-center items-center justify-center rounded-full bg-rose-100 px-2 text-xs font-medium leading-none text-rose-700">{group.violation_count}</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {group.violations.map((v, row) => <li key={row} className="px-4 py-3">
                    <div className="flex flex-wrap justify-between gap-2 text-sm">
                      <span className="font-medium text-slate-900">{v.employee_name}</span>
                      <span className="text-slate-500">{formatDateReadable(v.date)}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{v.violation_detail}</p>
                  </li>)}
                </ul>
              </section>)}
            </div>
            {data.skipped_regulation_types?.length > 0 && <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Not audited from time entries: {data.skipped_regulation_types.map((rule) => rule.regulation_type_name).join(", ")}.
            </p>}
          </div>}
        </Box>
      </Modal>
    </>
  );
}
